<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Dialog, Button, Fieldset, Select } from 'primevue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import CoilStackPositionSelect, { type CoilModel, type CoilStackPositionSelectModel } from 'shared/coils/components/CoilStackPositionSelect.vue';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { Assert } from 'shared/assert';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { computed, ref, useTemplateRef } from 'vue';
import { useMoveApi, type MoveRequest as MoveRequest } from '@/moves/api/moveApi';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useStorageAreaToCoilStack } from '../domain/coilStackPositionSelect';

defineExpose({
  show
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const actionFailedAlert = useActionFailedAlert();
const storageAreaToCoilStack = useStorageAreaToCoilStack();

const storageAreaStore = useStorageAreaStore();
const moveApi = useMoveApi();

const moveFromCoilStackPositionSelect = useTemplateRef('move-from-coil-stack');

const visible = ref(false);
const loading = ref(false);

const coilStacks = computed<CoilStackPositionSelectModel[]>(() => storageAreaStore.storageAreas
  .filter(s => s.type === StorageAreaType.CoilStack && s.coilStack)
  .map(s => storageAreaToCoilStack.transform(s))
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })));

const fromCoilStackId = ref<string>();

const fromCoilStack = computed(() => {
  if (fromCoilStackId.value === undefined) return;
  return coilStacks.value.find(s => s.id === fromCoilStackId.value);
});

const fromCoils = computed(() => {
  if (!fromCoilStack.value) return [];
  const coils = fromCoilStack.value.coils.filter(a => isMovable(a));
  return coils.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
});

const toCoilStackId = ref<string>();
const toCoilStack = computed(() => {
  if (toCoilStackId.value === undefined) return;
  return coilStacks.value.find(s => s.id === toCoilStackId.value);
});

const pickCoil = ref<CoilModel>();
const toCoil = ref<CoilModel>();

const sameStorageArea = computed(() => fromCoilStack.value?.id === toCoilStack.value?.id);

const moveRequest = computed(() => {
    const fromStorageAreaId = fromCoilStack.value?.id;
    if (!fromStorageAreaId) return;

    const toStorageAreaId = toCoilStack.value?.id;
    if (!toStorageAreaId) return;

    if (fromStorageAreaId === toStorageAreaId) return;

    const pickCoilValue = pickCoil.value;
    if (!pickCoilValue) return;

    const toCoilValue = toCoil.value;
    if (!toCoilValue) return;

    const request: MoveRequest = {
      fromStorageAreaId: fromStorageAreaId,
      toStorageAreaId: toStorageAreaId,
      coil: {
        coilId: pickCoilValue.id,
        position: toCoilValue.position,
        level: toCoilValue.level,
      }
    };

    return request;
});

const moveDisabled = computed(() => moveRequest.value ? false : true);

function show(action: ShowAction) {
  reset();

  fromCoilStackId.value = action.fromStorageAreaId;
  Assert.definied(fromCoilStack.value);

  toCoilStackId.value = action.toStorageAreaId;
  Assert.definied(toCoilStack.value);

  visible.value = true;
}

function reset() {
  fromCoilStackId.value = undefined;
  toCoilStackId.value = undefined;
  pickCoil.value = undefined;
  toCoil.value = undefined;
  loading.value = false;
  visible.value = false;
}

function isMovable(model: CoilModel) : boolean {
  const view = moveFromCoilStackPositionSelect.value;
  if (!view) return true;
  return view.isMovable(model);
}

async function onMoveClick() {
  try {
    loading.value = true;
    const request = moveRequest.value;
    Assert.definied(request);

    const response = await moveApi.execute(request);

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    const move = response.move;

    storageAreaStore.add(move.fromStorageArea);
    storageAreaStore.add(move.toStorageArea);

    show({
      fromStorageAreaId: move.fromStorageArea.id,
      toStorageAreaId: move.toStorageArea.id
    });

  } finally {
    loading.value = false;
  }
}

function onSwapStorageAreas() {
  const newToCoilStackId = fromCoilStackId.value;
  const newFromCoilStackId = toCoilStackId.value;

  fromCoilStackId.value = newFromCoilStackId;
  toCoilStackId.value = newToCoilStackId;
  pickCoil.value = undefined;
  toCoil.value = undefined;
}

export interface ShowAction {
  fromStorageAreaId: string;
  toStorageAreaId: string;
}

</script>

<template>
  <Dialog :header="translations.coils.moveDialog.title" v-model:visible="visible">
    <div class="move-coil-grid">
      <Fieldset :legend="translations.generic.from">
        <div class="move-from">

          <label>{{ translations.generic.storageArea }}</label>

          <Select autocomplete="off" v-model:modelValue="fromCoilStackId"
            :options="coilStacks" option-label="name" option-value="id" autofocus></Select>

          <label>{{ translations.generic.coil }}</label>

          <Select autocomplete="off" v-model:modelValue="pickCoil"
            :options="fromCoils" option-label="name" autofocus></Select>

          <CoilStackPositionSelect class="move-from-coil-stack" v-model:model-value="fromCoilStack"
            v-model:selected-coil="pickCoil"
            mode="take"
            ref="move-from-coil-stack"></CoilStackPositionSelect>
        </div>
      </Fieldset>
      <Fieldset :legend="translations.generic.to">
        <div class="move-to">

          <label>{{ translations.generic.storageArea }}</label>

          <Select autocomplete="off" v-model:modelValue="toCoilStackId"
            :options="coilStacks" option-label="name" option-value="id" autofocus></Select>

          <CoilStackPositionSelect v-model:model-value="toCoilStack"
            v-model:place-coil="pickCoil"
            v-model:selected-coil="toCoil"
            class="move-to-coil-stack"
            mode="place"
            :same-storage-area="sameStorageArea"></CoilStackPositionSelect>
        </div>
      </Fieldset>
    </div>
    <template #footer>
      <Button
        severity="secondary"
        :label="translations.generic.swap" @click="onSwapStorageAreas">
      </Button>
      <Button :disabled="moveDisabled"
        :label="translations.generic.move" @click="onMoveClick" :loading="loading"></Button>
    </template>
  </Dialog>
</template>

<style scoped>

.move-coil-grid {
  display: grid;
  grid-template-rows: auto auto auto auto auto;
  row-gap: 1rem;
}

.move-from {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  align-items: center;
  column-gap: .5rem;
}

.move-from-coil-stack {
  grid-column: 1/5;
}

.move-to {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: .5rem;
}

.move-to-coil-stack {
  grid-column: 1/3;
}

</style>
